import { useCallback, useEffect, useState } from "react";
import {
  isValidEmail,
  isValidNip,
  isValidPseudo,
  NIP_LENGTH,
  USER_ADMIN_ERROR,
  type AdminUser,
} from "@shared";
import { ApiFetchError } from "../api/client";
import * as api from "./adminApi";

interface Props {
  eventId: string;
  /** Le concours est-il proposé sur cette mission ? Décide de la colonne courriel. */
  contestEnabled: boolean;
}

/** Quelle boîte de dialogue est ouverte, et pour quel joueur. */
type DialogState =
  | { kind: "create" }
  | { kind: "nip"; user: AdminUser }
  | { kind: "email"; user: AdminUser }
  | null;

/**
 * Les joueurs de la mission consultée.
 *
 * Deux choses ne se voient pas dans cette liste, et c'est voulu :
 *
 * - le NIP. L'administration n'en voit jamais aucun, même haché. Elle peut
 *   seulement en POSER un nouveau, à communiquer de vive voix.
 * - une adresse courriel sur le compte lui-même. Un compte joueur ne porte
 *   aucune donnée personnelle. La colonne « Courriel » montre l'adresse qu'un
 *   parent a donnée POUR LE CONCOURS de cette mission — donc vide pour qui n'a
 *   pas participé, et propre à chaque mission.
 */
export default function UsersPanel({ eventId, contestEnabled }: Props) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setUsers(await api.listUsers(eventId));
    } catch {
      setError("Impossible de charger les joueurs.");
    }
  }, [eventId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Exécute une action, rafraîchit, et traduit le code d'erreur du serveur. */
  async function run(action: () => Promise<unknown>, succes: string): Promise<boolean> {
    setError(null);
    setMessage(null);
    try {
      await action();
      setMessage(succes);
      await refresh();
      return true;
    } catch (raison) {
      setError(messageFor(raison));
      return false;
    }
  }

  async function remove(user: AdminUser): Promise<void> {
    // La participation au concours SURVIT à la suppression du compte : le
    // consentement vient d'un parent, pas de l'enfant. Le dire ici, pas après.
    const avecCourriel =
      user.contestEmail !== null
        ? `\n\nSa participation au concours (${user.contestEmail}) N'EST PAS supprimée : ` +
          "le consentement a été donné par un parent. Pour la retirer, utilise l'icône ✉ " +
          "AVANT de supprimer le compte."
        : "";

    const confirme = window.confirm(
      `Supprimer le joueur « ${user.pseudo} » ?\n\n` +
        "Cela supprime aussi toutes ses parties et ses trouvailles. " +
        "Action irréversible." +
        avecCourriel,
    );
    if (!confirme) return;
    await run(() => api.deleteUser(user.id), `Joueur « ${user.pseudo} » supprimé.`);
  }

  async function removeEmail(user: AdminUser): Promise<void> {
    const confirme = window.confirm(
      `Retirer l'adresse « ${user.contestEmail ?? ""} » du concours ?\n\n` +
        "L'adresse est effacée de la base et la participation annulée. " +
        "C'est le retrait de consentement promis au parent.",
    );
    if (!confirme) return;
    await run(() => api.deleteUserEmail(eventId, user.id), "Adresse retirée du concours.");
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Joueurs</h2>
      <p className="panel__hint">
        L'administration ne voit <strong>aucun NIP</strong> : elle peut seulement en poser un
        nouveau. La colonne « Courriel » n'appartient pas au compte — c'est l'adresse donnée par un
        parent pour le <strong>concours de cette mission</strong>.
      </p>

      {error !== null && <p className="form__error">{error}</p>}
      {message !== null && <p className="form__saved">{message}</p>}

      <div className="button-row button-row--top">
        <button
          type="button"
          className="button button--primary"
          onClick={() => {
            setDialog({ kind: "create" });
            setMessage(null);
            setError(null);
          }}
        >
          + Ajouter un joueur
        </button>
      </div>

      {users === null && <p className="panel__hint">Chargement…</p>}

      {users !== null && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Pseudo</th>
                <th>Inscrit</th>
                <th>Parties</th>
                {contestEnabled && <th>Courriel (concours)</th>}
                <th className="table__actions-head">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.pseudo}</strong>
                    {/* Un compte qui n'a jamais ouvert le mot de bienvenue n'a
                        probablement jamais servi : utile avant de supprimer. */}
                    {user.welcomeSeenAt === null && (
                      <span className="table__tag" title="N'a jamais ouvert le mot de bienvenue">
                        jamais entré
                      </span>
                    )}
                  </td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td>{user.missionsPlayed}</td>
                  {contestEnabled && (
                    <td className="table__email">{user.contestEmail ?? <span>—</span>}</td>
                  )}
                  <td className="table__actions">
                    <button
                      type="button"
                      className="icon-button"
                      title={`Réinitialiser le NIP de ${user.pseudo}`}
                      aria-label={`Réinitialiser le NIP de ${user.pseudo}`}
                      onClick={() => {
                        setDialog({ kind: "nip", user });
                        setMessage(null);
                        setError(null);
                      }}
                    >
                      🔑
                    </button>

                    {/* Rien à modifier sans participation : l'administration ne
                        crée pas d'adresse, ce serait inventer un consentement. */}
                    {contestEnabled && (
                      <>
                        <button
                          type="button"
                          className="icon-button"
                          title={
                            user.contestEntryId === null
                              ? `${user.pseudo} n'a pas participé au concours`
                              : `Corriger l'adresse de ${user.pseudo}`
                          }
                          aria-label={`Corriger l'adresse de ${user.pseudo}`}
                          disabled={user.contestEntryId === null}
                          onClick={() => {
                            setDialog({ kind: "email", user });
                            setMessage(null);
                            setError(null);
                          }}
                        >
                          ✉
                        </button>
                        <button
                          type="button"
                          className="icon-button icon-button--danger"
                          title={
                            user.contestEntryId === null
                              ? `${user.pseudo} n'a pas participé au concours`
                              : `Retirer l'adresse de ${user.pseudo} du concours`
                          }
                          aria-label={`Retirer l'adresse de ${user.pseudo}`}
                          disabled={user.contestEntryId === null}
                          onClick={() => void removeEmail(user)}
                        >
                          ✉✕
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      className="icon-button icon-button--danger"
                      title={`Supprimer ${user.pseudo}`}
                      aria-label={`Supprimer ${user.pseudo}`}
                      onClick={() => void remove(user)}
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={contestEnabled ? 5 : 4} className="list__empty">
                    Aucun joueur inscrit.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {dialog?.kind === "create" && (
        <CreateUserDialog
          onClose={() => {
            setDialog(null);
          }}
          onSubmit={async (pseudo, nip) => {
            const ok = await run(() => api.createUser(pseudo, nip), `Joueur « ${pseudo} » créé.`);
            if (ok) setDialog(null);
          }}
        />
      )}

      {dialog?.kind === "nip" && (
        <NipDialog
          pseudo={dialog.user.pseudo}
          onClose={() => {
            setDialog(null);
          }}
          onSubmit={async (nip) => {
            const user = dialog.user;
            const ok = await run(
              () => api.resetNip(user.id, nip),
              `NIP de « ${user.pseudo} » remplacé. Communique-le-lui de vive voix.`,
            );
            if (ok) setDialog(null);
          }}
        />
      )}

      {dialog?.kind === "email" && (
        <EmailDialog
          pseudo={dialog.user.pseudo}
          current={dialog.user.contestEmail ?? ""}
          onClose={() => {
            setDialog(null);
          }}
          onSubmit={async (email) => {
            const user = dialog.user;
            const ok = await run(
              () => api.updateUserEmail(eventId, user.id, email),
              "Adresse corrigée.",
            );
            if (ok) setDialog(null);
          }}
        />
      )}
    </section>
  );
}

/** Le message vient du CODE renvoyé par le serveur, jamais de son texte. */
function messageFor(raison: unknown): string {
  const code = raison instanceof ApiFetchError ? raison.code : "";
  switch (code) {
    case USER_ADMIN_ERROR.pseudoTaken:
      return "Ce pseudo est déjà pris.";
    case USER_ADMIN_ERROR.invalidPseudo:
      return "Ce pseudo ne convient pas (2 à 20 caractères, lettres et chiffres).";
    case USER_ADMIN_ERROR.invalidNip:
      return `Le NIP doit être ${NIP_LENGTH} chiffres.`;
    case USER_ADMIN_ERROR.noContestEntry:
      return "Ce joueur n'a pas participé au concours : il n'y a pas d'adresse à modifier.";
    case USER_ADMIN_ERROR.emailTaken:
      return "Cette adresse participe déjà au concours.";
    case "invalid_email":
      return "Cette adresse ne ressemble pas à une adresse courriel.";
    default:
      return "L'opération a échoué. Réessaie.";
  }
}

// --- Boîtes de dialogue ------------------------------------------------------

/**
 * Cadre commun. Volontairement simple : un panneau au-dessus de la page, fermé
 * par Échap ou par « Annuler ». L'administration n'est pas un écran d'enfant,
 * elle n'a pas besoin d'animation.
 */
function DialogBox({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div className="dialog__backdrop" role="presentation" onClick={onClose}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <h3 className="dialog__title">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function CreateUserDialog({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (pseudo: string, nip: string) => Promise<void>;
}) {
  const [pseudo, setPseudo] = useState("");
  const [nip, setNip] = useState("");
  const pret = isValidPseudo(pseudo) && isValidNip(nip);

  return (
    <DialogBox title="Ajouter un joueur" onClose={onClose}>
      <p className="panel__hint">
        Pour dépanner sur place : un jeune qui n'arrive pas à s'inscrire. Note le NIP, il ne sera
        plus jamais affiché.
      </p>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit(pseudo, nip);
        }}
      >
        <label className="form__label" htmlFor="new-user-pseudo">
          Pseudo
        </label>
        <input
          id="new-user-pseudo"
          className="form__input"
          type="text"
          autoFocus
          value={pseudo}
          onChange={(e) => {
            setPseudo(e.target.value);
          }}
        />

        <label className="form__label" htmlFor="new-user-nip">
          NIP ({NIP_LENGTH} chiffres)
        </label>
        <input
          id="new-user-nip"
          className="form__input"
          type="text"
          inputMode="numeric"
          maxLength={NIP_LENGTH}
          value={nip}
          onChange={(e) => {
            setNip(e.target.value.replace(/\D/g, ""));
          }}
        />

        <div className="button-row">
          <button type="submit" className="button button--primary" disabled={!pret}>
            Créer
          </button>
          <button type="button" className="button button--ghost" onClick={onClose}>
            Annuler
          </button>
        </div>
      </form>
    </DialogBox>
  );
}

function NipDialog({
  pseudo,
  onClose,
  onSubmit,
}: {
  pseudo: string;
  onClose: () => void;
  onSubmit: (nip: string) => Promise<void>;
}) {
  const [nip, setNip] = useState("");

  return (
    <DialogBox title={`Nouveau NIP pour ${pseudo}`} onClose={onClose}>
      <p className="panel__hint">
        L'ancien NIP n'est pas consultable — il est haché. Tu en poses un nouveau, à communiquer de
        vive voix.
      </p>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit(nip);
        }}
      >
        <label className="form__label" htmlFor="reset-nip">
          NIP ({NIP_LENGTH} chiffres)
        </label>
        <input
          id="reset-nip"
          className="form__input"
          type="text"
          inputMode="numeric"
          maxLength={NIP_LENGTH}
          autoFocus
          value={nip}
          onChange={(e) => {
            setNip(e.target.value.replace(/\D/g, ""));
          }}
        />

        <div className="button-row">
          <button type="submit" className="button button--primary" disabled={!isValidNip(nip)}>
            Remplacer le NIP
          </button>
          <button type="button" className="button button--ghost" onClick={onClose}>
            Annuler
          </button>
        </div>
      </form>
    </DialogBox>
  );
}

function EmailDialog({
  pseudo,
  current,
  onClose,
  onSubmit,
}: {
  pseudo: string;
  current: string;
  onClose: () => void;
  onSubmit: (email: string) => Promise<void>;
}) {
  const [email, setEmail] = useState(current);

  return (
    <DialogBox title={`Adresse du concours — ${pseudo}`} onClose={onClose}>
      <p className="panel__hint">
        Corriger une faute de frappe signalée sur place. Le consentement déjà donné reste attaché à
        la participation : on corrige l'adresse, on n'en crée pas une nouvelle.
      </p>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit(email.trim());
        }}
      >
        <label className="form__label" htmlFor="edit-email">
          Adresse courriel
        </label>
        <input
          id="edit-email"
          className="form__input"
          type="email"
          inputMode="email"
          autoFocus
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
        />

        <div className="button-row">
          <button
            type="submit"
            className="button button--primary"
            disabled={!isValidEmail(email.trim()) || email.trim() === current}
          >
            Enregistrer
          </button>
          <button type="button" className="button button--ghost" onClick={onClose}>
            Annuler
          </button>
        </div>
      </form>
    </DialogBox>
  );
}
